import { useTranslation } from "react-i18next";
import {
  ActorIcon,
  SaveIcon,
  VideoIcon,
} from "@/components/ui/icon";
import { TextButton } from "@/components/common/text-button";
import { cn } from "@/lib/utils";
import { TextInput } from "@/components/common/text-input";
import { DeleteIcon, EditGrayIcon, IdCardIcon, VoiceIdIcon } from "@/components/agent-dashboard/assets";
import { TruncatedText } from "@/components/common/truncated-text";
import { IconButton } from '@/components/common/icon-button';
import { ChooseVoiceModal } from "@/components/agent-dashboard-user-detail/choose-voice-modal";
import { DeleteConfirmModal, Spinner } from "@/components/ui/modal";
import { CopyClipboard } from "@/components/common/copy-clipboard";
import { Edit3Icon, TrashIcon, X } from "lucide-react";
import { Image } from '@/components/common/image';
import { AudioPlayButton } from "../common/audio-play-button";
import { AgentV2TemplateDetailData } from "@/services/agent-v2-template-detail";
import { SETTING_TABS } from "@/manager/manager/agent-dashboard-user-management-manager";
import { useMemo } from "react";

interface PanelParams {
  template: AgentV2TemplateDetailData;
  activeTab?: string;
  loading?: boolean;
  deleteLoading?: boolean;
  onEdit?: () => void;
  onClose?: () => void;
  onDelete?: () => Promise<void>;
}

export default function RightPanel({
  template = {} as AgentV2TemplateDetailData,
  activeTab = "",
  loading = false,
  deleteLoading = false,
  onEdit,
  onClose,
  onDelete,
}: PanelParams) {
  const { t } = useTranslation();

  const handleSave = (e) => {
    e.stopPropagation();
    onEdit?.();
  }

  const handleDelete = async () => {
    await onDelete?.();
  }

  // 简化信息展示（图文、文章）
  const isMinimalTab = useMemo(() => {
    return activeTab === SETTING_TABS.article || activeTab === SETTING_TABS.image;
  }, [activeTab]);
  

  const CommonInfo = (
    <>
      {/* 风格 */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-sm text-i-sub">
            {t("agent_dashboard.user_detail.template.detail_modal.style")}
          </span>
        </div>
        <div className="w-full flex items-center gap-3">
          <div className="w-[6.5625rem] h-[8.75rem] overflow-hidden flex-shrink-0">
            <Image src={template.coverUrl} className="size-full rounded-[12px] object-cover" />
          </div>
          <div className="w-full">
            <div className="text-sm text-[rgba(16,24,40,1)] font-medium">{template.templateName}</div>
            <div className="w-full flex items-center gap-1">
              <TruncatedText
                text={template.styleModelId}
                className="max-w-[100%] text-sm text-[rgba(16,24,40,1)]"
              />
              <CopyClipboard text={template.styleModelId} iconClassName="size-4" />
            </div>
          </div>
        </div>
      </div>
      {/* 数字人 */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-sm text-i-sub">
            {t("agent_dashboard.user_detail.template.detail_modal.actor")}
          </span>
        </div>
        <div className="w-full flex items-center gap-3">
          <div className="w-[6.5625rem] h-[8.75rem] overflow-hidden flex-shrink-0">
            <Image src={template.actorPicUrl} className="size-full rounded-[12px] object-cover" />
          </div>
          <div className="w-full">
            <div className="text-sm text-[rgba(16,24,40,1)] font-medium">{template.actorName}</div>
            <div className="w-full flex items-center gap-1">
              <TruncatedText
                text={template.actorModelId}
                className="max-w-[80%] text-sm text-[rgba(16,24,40,1)]"
              />
              <CopyClipboard text={template.actorModelId} iconClassName="size-4" />
            </div>
          </div>
        </div>
      </div>
      {/* Voice ID */}
      <div className="flex flex-col flex-1 space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-sm text-i-sub">
            {t("agent_dashboard.user_detail.template.detail_modal.voice")}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0">
            <AudioPlayButton
              key={template.voiceModelId}
              iconClassName="size-10"
              disabled={!template.voiceSampleUrl}
              url={template.voiceSampleUrl}
            />
          </div>
          <div className="w-[90%]">
            <div className="text-sm text-[rgba(16,24,40,1)] font-medium">{template.voiceName}</div>
            <div className="w-full flex items-center gap-1">
              <TruncatedText
                text={template.voiceModelId}
                className="max-w-[80%] text-sm text-[rgba(16,24,40,1)]"
              />
              <CopyClipboard text={template.voiceModelId} iconClassName="size-4" />
            </div>
          </div>
        </div>
      </div>
    </>
  )

  return (
    <div className="w-[26.25rem] bg-i-white flex flex-col gap-4 p-6">
      {loading && <Skeleton />}
      {!loading && <>
        <div className="flex justify-between items-center">
          <span className="text-xl font-semibold text-[rgba(16,24,40,1)]">
            {t("agent_dashboard.user_detail.template.detail_modal.title")}
          </span>
          <button
            className="w-10 h-10 flex justify-center items-center hover:bg-gray-100 border border-[rgba(208,213,221,1)] rounded-sm shadow-[0px_1px_2px_rgba(16,24,40,0.05)]"
            tabIndex={-1}
            onClick={() => {
              onClose?.();
            }}
          >
            <X className="w-4 h-4 text-[rgba(71,84,103,1)]" />
          </button>
        </div>
        {/* 模板ID */}
        <div className={cn(
          "space-y-2",
          isMinimalTab && "flex-1"
        )}>
          <div className="flex items-center gap-2">
            <span className="text-sm text-i-sub">
              {t("agent_dashboard.user_detail.template.detail_modal.template_id")}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <TruncatedText
              text={template.uuid}
              className="max-w-[70%] text-sm text-[rgba(16,24,40,1)] text-medium"
            />
            <CopyClipboard text={template.uuid} iconClassName="size-4" />
          </div>
        </div>
        {!isMinimalTab && CommonInfo}
        {/* 操作按钮 */}
        <div className={cn(
          "flex justify-center gap-3",
          isMinimalTab && "justify-end"
        )}>
          <DeleteConfirmModal
            trigger={
              <TextButton
                icon={<DeleteIcon className="w-5 h-5 text-[rgba(52,64,84,1)]" />}
                label={t('agent_dashboard.user_detail.template.detail_modal.delete')}
                className='h-10 w-[11.25rem] bg-white border border-[rgba(213,215,218,1)] hover:bg-gray-50 font-medium text-sm !text-[rgba(52,64,84,1)]'
              />
            }
            loading={deleteLoading}
            onConfirm={handleDelete}
          />
          {!isMinimalTab && <TextButton
            onClick={handleSave}
            icon={<Edit3Icon className="w-5 h-5" />}
            label={t("agent_dashboard.user_detail.template.detail_modal.edit")}
            className={cn(
              "justify-center items-center gap-2 h-10 w-[11.25rem] bg-i-normal shadow-[0px_1px_2px_rgba(16,24,40,0.05)] rounded-sm transition-colors hover:bg-[rgba(40,47,62,1)] text-sm"
            )}
          />}
        </div>
      </>}
    </div>
  );
}

// 骨架屏组件
const Skeleton = () => {
  return (
    <div className="w-full space-y-6 relative">
      {/* 标题骨架 */}
      <div className="flex justify-between items-center">
        <div className="h-6 w-40 bg-gray-200 rounded animate-pulse"></div>
        <div className="w-10 h-10 bg-gray-200 rounded-sm animate-pulse"></div>
      </div>

      {/* 模板ID骨架 */}
      <div className="space-y-2">
        <div className="h-4 w-32 bg-gray-200 rounded animate-pulse"></div>
        <div className="flex items-center gap-1">
          <div className="h-4 w-[70%] bg-gray-200 rounded animate-pulse"></div>
          <div className="h-4 w-4 bg-gray-200 rounded animate-pulse"></div>
        </div>
      </div>

      {/* 风格骨架 */}
      <div className="space-y-2">
        <div className="h-4 w-32 bg-gray-200 rounded animate-pulse"></div>
        <div className="flex items-center gap-3">
          <div className="w-[6.5625rem] h-[8.75rem] flex-shrink-0 bg-gray-200 rounded-[12px] animate-pulse"></div>
          <div className="w-full space-y-2">
            <div className="h-4 w-24 bg-gray-200 rounded animate-pulse"></div>
            <div className="flex items-center gap-1">
              <div className="h-4 w-[90%] bg-gray-200 rounded animate-pulse"></div>
              <div className="h-4 w-4 bg-gray-200 rounded animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>

      {/* 数字人骨架 */}
      <div className="space-y-2">
        <div className="h-4 w-32 bg-gray-200 rounded animate-pulse"></div>
        <div className="flex items-center gap-3">
          <div className="w-[6.5625rem] h-[8.75rem] flex-shrink-0 bg-gray-200 rounded-[12px] animate-pulse"></div>
          <div className="w-full space-y-2">
            <div className="h-4 w-24 bg-gray-200 rounded animate-pulse"></div>
            <div className="flex items-center gap-1">
              <div className="h-4 w-[80%] bg-gray-200 rounded animate-pulse"></div>
              <div className="h-4 w-4 bg-gray-200 rounded animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>

      {/* 声音骨架 */}
      <div className="flex flex-col flex-1 space-y-2">
        <div className="h-4 w-32 bg-gray-200 rounded animate-pulse"></div>
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 h-10 w-10 bg-gray-200 rounded animate-pulse"></div>
          <div className="w-[90%] space-y-2">
            <div className="h-4 w-24 bg-gray-200 rounded animate-pulse"></div>
            <div className="flex items-center gap-1">
              <div className="h-4 w-[80%] bg-gray-200 rounded animate-pulse"></div>
              <div className="h-4 w-4 bg-gray-200 rounded animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>

      {/* 操作按钮骨架 */}
      <div className="flex justify-center gap-3 absolute -bottom-32 -left-30">
        <div className="h-10 w-[11.25rem] bg-gray-200 rounded-[8px] animate-pulse "></div>
        <div className="h-10 w-[11.25rem] bg-gray-200 rounded-[8px] animate-pulse"></div>
      </div>
    </div>
  );
};